import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faTimes } from '@fortawesome/free-solid-svg-icons';
import { faAndroid } from '@fortawesome/free-brands-svg-icons';

const FloatingDownloadButton = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Auto-expand on mount after 2 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExpanded(true);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  // Auto-collapse after 5 seconds of being expanded
  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => {
        setIsExpanded(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  if (!isVisible) return null;

  return (
    <>
      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes pulse-ring {
          0% {
            transform: scale(0.8);
            opacity: 1;
          }
          100% {
            transform: scale(1.4);
            opacity: 0;
          }
        }

        @keyframes shake {
          0%, 100% { transform: rotate(0deg); }
          25% { transform: rotate(-10deg); }
          75% { transform: rotate(10deg); }
        }

        .float-animation {
          animation: float 3s ease-in-out infinite;
        }

        .pulse-ring {
          animation: pulse-ring 2s cubic-bezier(0.215, 0.61, 0.355, 1) infinite;
        }

        .shake-animation {
          animation: shake 0.5s ease-in-out;
        }
      `}</style>

      <div className="fixed bottom-6 left-6 z-[9999] flex items-end gap-3">
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, x: -20, scale: 0.8 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              exit={{ opacity: 0, x: -20, scale: 0.8 }}
              transition={{ duration: 0.3, type: "spring" }}
              className="relative"
            >
              {/* Expanded Card */}
              <div className="relative bg-gradient-to-br from-green-500 via-green-600 to-emerald-600 rounded-2xl shadow-2xl overflow-hidden border-2 border-white/30">
                {/* Background decorations */}
                <div className="absolute inset-0 overflow-hidden">
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
                  <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-emerald-400/20 rounded-full blur-2xl"></div>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setIsExpanded(false)}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-all duration-200 z-10 group"
                  aria-label="Close"
                >
                  <FontAwesomeIcon 
                    icon={faTimes} 
                    className="text-xs text-white group-hover:rotate-90 transition-transform duration-200" 
                  />
                </button>

                {/* Content */}
                <div className="relative p-5 pr-8">
                  <div className="flex items-start gap-3">
                    {/* App Icon */}
                    <div className="flex-shrink-0 w-14 h-14 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center border border-white/30 shadow-lg">
                      <FontAwesomeIcon icon={faAndroid} className="text-2xl text-white" />
                    </div>

                    {/* Text Content */}
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-base mb-1 leading-tight">
                        Download IINSAF App
                      </h3>
                      <p className="text-white/90 text-xs mb-3 leading-relaxed">
                        Get the app for seamless experience
                      </p>

                      {/* Download Button */}
                      <a
                        href="/iinsaf.apk"
                        download="iinsaf.apk"
                        className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-lg text-green-600 font-semibold text-sm hover:bg-green-50 transition-all duration-200 shadow-md hover:shadow-lg hover:scale-105 active:scale-95 group"
                      >
                        <FontAwesomeIcon 
                          icon={faDownload} 
                          className="text-sm group-hover:animate-bounce" 
                        />
                        <span>Download Now</span>
                      </a>

                      {/* Features */}
                      <div className="flex gap-2 mt-3 flex-wrap">
                        <span className="text-[10px] px-2 py-0.5 bg-white/20 rounded-full text-white border border-white/30">
                          Free
                        </span>
                        <span className="text-[10px] px-2 py-0.5 bg-white/20 rounded-full text-white border border-white/30">
                          Secure
                        </span>
                        <span className="text-[10px] px-2 py-0.5 bg-white/20 rounded-full text-white border border-white/30">
                          Fast
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Decorative bottom wave */}
                <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-green-400 via-emerald-400 to-green-400"></div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Floating Button */}
        <motion.button
          onClick={() => setIsExpanded(!isExpanded)}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          className="relative float-animation group"
        >
          {/* Pulse rings */}
          <div className="absolute inset-0 rounded-full bg-green-500 pulse-ring"></div>
          <div className="absolute inset-0 rounded-full bg-green-500 pulse-ring" style={{ animationDelay: '1s' }}></div>

          {/* Main button */}
          <div className="relative w-16 h-16 rounded-full bg-gradient-to-br from-green-500 via-green-600 to-emerald-600 shadow-2xl flex flex-col items-center justify-center border-4 border-white group-hover:border-green-100 transition-all duration-300 group-hover:shadow-green-500/50">
            <FontAwesomeIcon 
              icon={faDownload} 
              className="text-white text-xl mb-0.5 group-hover:animate-bounce" 
            />
            <span className="text-[9px] text-white font-bold uppercase tracking-wide">
              App
            </span>
          </div>

          {/* Notification badge */}
          <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center border-2 border-white shadow-lg animate-pulse">
            <FontAwesomeIcon icon={faAndroid} className="text-white text-[10px]" />
          </div>

          {/* Glow effect */}
          <div className="absolute inset-0 rounded-full bg-green-400 opacity-0 group-hover:opacity-30 blur-xl transition-opacity duration-300"></div>
        </motion.button>
      </div>
    </>
  );
};

export default FloatingDownloadButton;

